import React, { useCallback, useEffect, useState, memo } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import CostCenterSelect from '../../../Employees/Form/FormComponents/CostCenterSelect'
import OfficeSelect from '../../../Employees/Form/FormComponents/OfficeSelect'
import { emptyDate } from '../../../../utils/emptyDate'
import CustomDatePicker from '../../../Forms/CustomDatePicker'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { emptyGuid, isEmptyGuid } from '../../../../utils/emptyGuid'
import { StyledValidationMessage } from '../formHelper'
import MandatoryField from '../../../MandatoryField/MandatoryField'
import useStores from '../../../../stores/useStore'
import { getIsoDate } from '../../../../utils/getIsoDate'
import {
  defaultTemplate,
  checkConfigOption,
} from '../../../../utils/assetTypeConfigurations'
import useMountEffect from '../../../../utils/hooks/useMountEffect'
import { isEmptyStr } from '../../../../utils/objectutils'
import BPSelectModal from '../../../BPSelect/BPSelectModal'
import { cloneDeep } from 'lodash'
import FindIcon from '../../../../assets/icons/find.png'
import { getEmployee } from '../../../../utils/employeeLoader'

const StyledImage = styled.div`
  height: 24px;
  width: 24px;
  background-size: contain;
  background-image: none;
  background-image: url(${(props) => (props.image ? props.image : '')});
`
const StyledWrapper = styled.div`
  .form-label {
    white-space: nowrap;
  }
`
const ResponsibleSelect = ({
  setSelectedBPId,
  handleBPWasChanged,
  disabled,
}) => {
  const [showAddBPsModal, setShowAddBPsModal] = useState(false)
  const [selectedBPs, setSelectedBPs] = useState([])

  useEffect(() => {
    if (!selectedBPs.length) {
      setSelectedBPId(emptyGuid)
    } else {
      setSelectedBPId(selectedBPs[0].id)
    }
  }, [setSelectedBPId, selectedBPs])
  return (
    <>
      <StyledImage
        style={{ cursor: 'pointer', marginLeft: '10px' }}
        image={FindIcon}
        onClick={() => !disabled && setShowAddBPsModal(true)}
      />
      {showAddBPsModal ? (
        <BPSelectModal
          selectedBPs={selectedBPs}
          setSelectedBPs={setSelectedBPs}
          setShowAddBPsModal={setShowAddBPsModal}
          showAddBPsModal={showAddBPsModal}
          handleSave={() => {
            setShowAddBPsModal(false)
            handleBPWasChanged()
          }}
          singleSelection={true}
          officeIndependent={true}
        />
      ) : null}
    </>
  )
}

const MainData = memo(
  ({
    mainData,
    setMainDataByKey,
    formErrors,
    resetErrorByFormKey,
    configuration,
  }) => {
    const { costCenterStore } = useStores()
    const [timeSpanStartDate, setTimeSpanStartDate] = useState('')
    const [timeSpanEndDate, setTimeSpanEndDate] = useState('')
    const [defaultConfiguration, setDefaultConfiguration] = useState(
      cloneDeep(defaultTemplate)
    )
    const [selectedResponsibleBPId, setSelectedResponibleBPId] =
      useState(emptyGuid)
    const [selectedResponsibleBP, setSelectedResponsibleBP] =
      useState(emptyGuid)

    useMountEffect(() => {
      setDefaultConfiguration(JSON.parse(configuration))
      setSelectedResponsibleBP(mainData.responisble?.id ?? emptyGuid)
    })
    useEffect(() => {
      setDefaultConfiguration(JSON.parse(configuration))
    }, [configuration])
    const setDateWithString = useCallback((setDate, dateISOString) => {
      if (dateISOString && dateISOString !== emptyDate) {
        setDate(new Date(dateISOString))
      } else {
        setDate('')
      }
    }, [])

    useEffect(() => {
      setDateWithString(setTimeSpanStartDate, mainData.timeSpanStartDate)
    }, [mainData.timeSpanStartDate, setDateWithString])

    useEffect(() => {
      setDateWithString(setTimeSpanEndDate, mainData.timeSpanEndDate)
    }, [mainData.timeSpanEndDate, setDateWithString])

    const handleChangeCostCenter = (costCenter) => {
      setMainDataByKey('costCenter.id', costCenter.id)
      resetErrorByFormKey('costCenter')
    }

    const handleChangeOffice = (value) => {
      setMainDataByKey('office.id', value)
      autoChangeCostCenter(value)
      resetErrorByFormKey('office')
    }

    const autoChangeCostCenter = async (value) => {
      setMainDataByKey('costCenter.id', emptyGuid)
      const filteredCostCenters = await costCenterStore.getFilteredCostCenters(
        '',
        value
      )
      if (filteredCostCenters.length === 1) {
        setMainDataByKey('costCenter.id', filteredCostCenters[0].id)
      } else {
        setMainDataByKey('costCenter.id', emptyGuid)
      }
      resetErrorByFormKey('costCenter')
    }
    const handleChangeResponsible = async () => {
      setMainDataByKey('responisble.displayName', '')
      if (isEmptyGuid(selectedResponsibleBPId)) {
        setSelectedResponsibleBP(null)
      } else if (
        (selectedResponsibleBP?.id ?? emptyGuid) != selectedResponsibleBPId
      ) {
        let bpDetail = await getEmployee(selectedResponsibleBPId)
        setSelectedResponsibleBP(bpDetail)
        setMainDataByKey('responisble.displayName', bpDetail?.displayName ?? '')
      }
      setMainDataByKey('responisble.id', selectedResponsibleBPId)
      resetErrorByFormKey('responisble')
    }
    const handleChangeDate = (name) => (date) => {
      setMainDataByKey(name, (date && getIsoDate(date)) || '')
      resetErrorByFormKey(name)
    }
    const handleChange = (e) => {
      const { name, value } = e.target
      setMainDataByKey(name, value)
      resetErrorByFormKey(name)
    }
    const { t } = useTranslation()
    const checkNumberIsMandatory = () => {
      return isEmptyStr(defaultConfiguration.number?.numberrange)
    }
    return (
      <>
        <StyledWrapper className='spacing-down'>
          <Row>
            <Col lg='3' sm='6' xs='12'>
              <Form.Group className='mb-3'>
                <Form.Label>{t('assetFormMainData:name')} *</Form.Label>
                <div>
                  <Form.Control
                    type='text'
                    placeholder={t('assetFormMainData:name')}
                    name='name'
                    onChange={handleChange}
                    value={mainData.name}
                    isInvalid={!!formErrors['name']}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {formErrors['name'] && formErrors['name'].msg}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>
            <Col sm='6' lg='3'>
              <Form.Group className='mb-3'>
                <Form.Label>
                  {t('assetFormMainData:number')}
                  {checkNumberIsMandatory() ? '*' : ''}
                </Form.Label>
                <Form.Control
                  type='text'
                  placeholder={t('assetFormMainData:number')}
                  name='number'
                  onChange={handleChange}
                  value={mainData.number}
                  isInvalid={!!formErrors['number']}
                />
                <Form.Control.Feedback type='invalid'>
                  {formErrors['number'] && formErrors['number'].msg}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col sm='6' lg='3'>
              <Form.Group className='mb-3'>
                <Form.Label>{t('assetFormMainData:number2')}</Form.Label>
                <Form.Control
                  type='text'
                  placeholder={t('assetFormMainData:number2')}
                  name='number2'
                  onChange={handleChange}
                  value={mainData.number2}
                  isInvalid={!!formErrors['number2']}
                />
              </Form.Group>
            </Col>
          </Row>
          {!checkConfigOption(
            'disabled',
            defaultConfiguration.timeSpan?.option
          ) && (
            <Row>
              <Col sm='6'>
                <Form.Group className='mb-3'>
                  <Form.Label>
                    {t('assetFormMainData:timespanStartdate')}
                    {checkConfigOption(
                      'mandatory',
                      defaultConfiguration.timeSpan?.option
                    )
                      ? '*'
                      : ''}
                  </Form.Label>
                  <CustomDatePicker
                    dateFormat='dd.MM.yyyy'
                    placeholderText={t('date')}
                    onChange={handleChangeDate('timeSpanStartDate')}
                    showYearDropdown
                    selected={timeSpanStartDate}
                    isInvalid={!!formErrors['timeSpanStartDate']}
                  />
                  {!!formErrors['timeSpanStartDate'] && (
                    <StyledValidationMessage>
                      {formErrors['timeSpanStartDate'].msg}
                    </StyledValidationMessage>
                  )}
                </Form.Group>
              </Col>
              <Col sm='6'>
                <Form.Group className='mb-3'>
                  <Form.Label>
                    {t('assetFormMainData:timespanEnddate')}
                    {checkConfigOption(
                      'mandatory',
                      defaultConfiguration.timeSpan?.option
                    )
                      ? '*'
                      : ''}
                  </Form.Label>
                  <CustomDatePicker
                    dateFormat='dd.MM.yyyy'
                    placeholderText={t('date')}
                    onChange={handleChangeDate('timeSpanEndDate')}
                    parseISO
                    showYearDropdown
                    selected={timeSpanEndDate}
                    isInvalid={!!formErrors['timeSpanEndDate']}
                  />
                  {!!formErrors['timeSpanEndDate'] && (
                    <StyledValidationMessage>
                      {formErrors['timeSpanEndDate'].msg}
                    </StyledValidationMessage>
                  )}
                </Form.Group>
              </Col>
            </Row>
          )}
          {!checkConfigOption(
            'disabled',
            defaultConfiguration.responsiblePerson?.option
          ) && (
            <Row>
              <Col sm='6'>
                <Form.Group className='mb-3'>
                  <Form.Label>
                    {t('assetFormMainData:responsible')}
                    {checkConfigOption(
                      'mandatory',
                      defaultConfiguration.responsiblePerson?.option
                    )
                      ? '*'
                      : ''}
                  </Form.Label>
                  <div className='d-flex align-items-start'>
                    <Form.Control
                      type='text'
                      name='responisbledisplayName'
                      value={mainData.responisble?.displayName ?? ''}
                      disabled={true}
                    />
                    <ResponsibleSelect
                      setSelectedBPId={setSelectedResponibleBPId}
                      handleBPWasChanged={handleChangeResponsible}
                    />
                  </div>
                  {!!formErrors['responisble'] && (
                    <StyledValidationMessage>
                      {formErrors['responisble'].msg}
                    </StyledValidationMessage>
                  )}
                </Form.Group>
              </Col>
            </Row>
          )}
          {!checkConfigOption(
            'disabled',
            defaultConfiguration.comment?.option
          ) && (
            <Row>
              <Col sm='6'>
                <Form.Group className='mb-3'>
                  <Form.Label>
                    {t('assetFormMainData:comment')}
                    {checkConfigOption(
                      'mandatory',
                      defaultConfiguration.comment?.option
                    )
                      ? '*'
                      : ''}
                  </Form.Label>
                  <Form.Control
                    as='textarea'
                    placeholder={t('assetFormMainData:comment')}
                    name='comment'
                    onChange={handleChange}
                    value={mainData.comment || ''}
                    isInvalid={!!formErrors['comment']}
                  />
                  {!!formErrors['comment'] && (
                    <StyledValidationMessage>
                      {formErrors['comment'].msg}
                    </StyledValidationMessage>
                  )}
                </Form.Group>
              </Col>
            </Row>
          )}
          {isEmptyGuid(mainData.group?.id) && (
            <Row>
              <Col sm='6'>
                <OfficeSelect
                  id='assetFormMainDataOfficeTree'
                  label={`${t('assetFormMainData:mainOffice')} *`}
                  errorMessage={
                    formErrors['office'] && formErrors['office'].msg
                  }
                  onChange={handleChangeOffice}
                  value={mainData.office.id}
                  isInvalid={!!formErrors['office']}
                  withSearch
                  allowParentSelection={false}
                />
              </Col>
              <Col sm='6'>
                <CostCenterSelect
                  id='assetFormMainDataConstCenterTree'
                  label={`${t('assetFormMainData:mainCostcenter')} *`}
                  errorMessage={
                    formErrors['costCenter'] && formErrors['costCenter'].msg
                  }
                  onChange={handleChangeCostCenter}
                  value={mainData.costCenter.id}
                  isInvalid={formErrors['costCenter']}
                  withSearch
                  filterMainOfficeId={
                    mainData.office.id !== emptyGuid ? mainData.office.id : ''
                  }
                />
              </Col>
            </Row>
          )}
          <MandatoryField />
        </StyledWrapper>
      </>
    )
  }
)

export default MainData
